import { createReducer } from '../lib/createReducer';

const initialState = {
  operationsMetrics: [],
  companiesWithWorkflows: [],
  apiCalls: [],
  workflowOperations: [],
  workflowCompanyUsage: [],
  workflowCompanyUsageError: null,
  workflowAlarms: [],
  operationsTeams: { teams: [], members: [], serviceUsers: [] },
  operationsTeamsError: null,
  operationsTeamsLoaded: false,
  hubspotCompanyMappings: {
    hubspotCompanies: [],
    gravityCompanies: []
  },
  hubspotCompanyMappingsError: null,
  hubspotCompanyMappingsLoaded: false,
  pricingCalculatorDefaults: null,
  pricingCalculatorDefaultsError: null
};

const setPricingCalculatorDefaults = (state, payload) => ({
  ...state,
  pricingCalculatorDefaults: payload?.data || null,
  pricingCalculatorDefaultsError: null
});

const setWorkflowAlarms = (state, payload) => ({
  ...state,
  workflowAlarms: payload?.data || []
});

export default createReducer(initialState, {
  GET_OPERATIONS_TEAMS_SUCCESS: (state, payload) => ({ ...state, operationsTeams: payload.data, operationsTeamsError: null, operationsTeamsLoaded: true }),
  GET_OPERATIONS_TEAMS_FAIL: (state, payload) => ({ ...state, operationsTeamsError: payload?.message || 'Teams could not be loaded.' }),
  UPDATE_OPERATIONS_TEAM_LEAD_SUCCESS: (state, { data }) => ({ ...state, operationsTeams: { ...state.operationsTeams, teams: state.operationsTeams.teams.map((team) => (team.hubspotTeamKey === data.hubspotTeamKey ? data : team)) } }),
  APPLY_OPERATIONS_TEAM_SYNC_SUCCESS: (state) => ({ ...state, operationsTeamsLoaded: false, hubspotCompanyMappingsLoaded: false }),
  UPDATE_OPERATIONS_COMPANY_TEAM_SUCCESS: (state, { data }) => ({
    ...state,
    hubspotCompanyMappings: { ...state.hubspotCompanyMappings, hubspotCompanies: state.hubspotCompanyMappings.hubspotCompanies.map((company) => (company.companyId === data.companyId && company.hubspotCompanyId === data.hubspotCompanyId ? { ...company, savedTeamKey: data.savedTeamKey, teamSyncedOn: data.teamSyncedOn } : company)) }
  }),
  UPDATE_OPERATIONS_TEAM_MEMBERS_SUCCESS: (state, { data }) => ({
    ...state,
    operationsTeams: {
      ...state.operationsTeams,
      members: [...state.operationsTeams.members.filter((member) => !data.removedServiceUserIds.includes(member.serviceUserId) && !data.members.some((updated) => updated.serviceUserId === member.serviceUserId)), ...data.members]
    }
  }),
  UPDATE_OPERATIONS_OWNERSHIP_SUCCESS: (state, payload) => ({ ...state, hubspotCompanyMappings: { ...state.hubspotCompanyMappings, hubspotCompanies: state.hubspotCompanyMappings.hubspotCompanies.map((company) => (company.companyId === payload.data.companyId ? { ...company, operationsEngineerIds: payload.data.operationsEngineerIds, ownershipMode: payload.data.ownershipMode } : company)) } }),
  UPDATE_OPERATIONS_SNOOZE_SUCCESS: (state, { data }) => ({
    ...state,
    companiesWithWorkflows: state.companiesWithWorkflows.map((company) => (data.scope === 'company' && company.companyId === data.id ? { ...company, snoozedUntil: data.snoozedUntil } : company)),
    workflowAlarms: state.workflowAlarms.map((alarm) => (data.scope === 'company' && alarm.companyId === data.id ? { ...alarm, companySnoozedUntil: data.snoozedUntil } : data.scope === 'workflow' && alarm.workflowId === data.id ? { ...alarm, snoozedUntil: data.snoozedUntil } : alarm))
  }),
  GET_OPERATION_METRICS_SUCCESS: (state, payload) => ({
    ...state,
    operationsMetrics: payload?.data || []
  }),
  GET_COMPANIES_WITH_WORKFLOWS_SUCCESS: (state, payload) => ({
    ...state,
    companiesWithWorkflows: payload?.data || []
  }),
  GET_API_CALLS_SUCCESS: (state, payload) => ({
    ...state,
    apiCalls: payload?.data || []
  }),
  GET_WORKFLOW_OPERATIONS_SUCCESS: (state, payload) => ({
    ...state,
    workflowOperations: payload?.data || []
  }),
  GET_WORKFLOW_COMPANY_USAGE_SUCCESS: (state, payload) => ({
    ...state,
    workflowCompanyUsage: payload?.data || [],
    workflowCompanyUsageError: null
  }),
  GET_WORKFLOW_COMPANY_USAGE_FAIL: (state, payload) => ({
    ...state,
    workflowCompanyUsageError: payload?.message || 'Failed to load workflow company usage.'
  }),
  GET_WORKFLOW_ALARMS_SUCCESS: setWorkflowAlarms,
  GET_HUBSPOT_COMPANY_MAPPINGS_SUCCESS: (state, payload) => ({
    ...state,
    hubspotCompanyMappings: payload?.data || { hubspotCompanies: [], gravityCompanies: [] },
    hubspotCompanyMappingsError: null,
    hubspotCompanyMappingsLoaded: true
  }),
  GET_HUBSPOT_COMPANY_MAPPINGS_FAIL: (state, payload) => ({
    ...state,
    hubspotCompanyMappingsError: payload?.data?.message || payload?.message || 'HubSpot companies could not be loaded.'
  }),
  UPDATE_HUBSPOT_COMPANY_MAPPING_SUCCESS: (state, payload) => ({
    ...state,
    hubspotCompanyMappings: {
      ...state.hubspotCompanyMappings,
      hubspotCompanies: state.hubspotCompanyMappings.hubspotCompanies.map((company) =>
        company.hubspotCompanyId === payload?.data?.hubspotCompanyId
          ? {
              ...company,
              companyId: payload.data.companyId,
              operationsEngineerIds: payload.data.operationsEngineerIds,
              ownershipMode: payload.data.ownershipMode,
              savedTeamKey: payload.data.savedTeamKey,
              teamSyncedOn: payload.data.teamSyncedOn
            }
          : company
      )
    }
  }),
  SET_WORKFLOW_ALARMS_SUCCESS: setWorkflowAlarms,
  GET_PRICING_CALCULATOR_DEFAULTS_SUCCESS: setPricingCalculatorDefaults,
  GET_PRICING_CALCULATOR_DEFAULTS_FAIL: (state, payload) => ({
    ...state,
    pricingCalculatorDefaultsError: payload?.data?.message || payload?.message || 'Pricing calculator defaults could not be loaded.'
  }),
  UPDATE_PRICING_CALCULATOR_DEFAULTS_SUCCESS: setPricingCalculatorDefaults,
  ENABLE_WORKFLOW_ALARM_SUCCESS: (state, payload) => ({
    ...state,
    workflowAlarms: state.workflowAlarms.some((alarm) => alarm.workflowId === payload?.data?.workflowId) ? state.workflowAlarms.map((alarm) => (alarm.workflowId === payload.data.workflowId ? { ...alarm, ...payload.data } : alarm)) : [...state.workflowAlarms, payload.data]
  }),
  DISABLE_WORKFLOW_ALARM_SUCCESS: (state, payload) => ({
    ...state,
    workflowAlarms: state.workflowAlarms.filter((alarm) => alarm.workflowId !== payload?.data?.workflowId)
  })
});
